import React, { useContext, useEffect, useState } from 'react'
import { Card, Form, Button, Spinner, Alert, Modal } from 'react-bootstrap'
import { PageTitle } from '../../../_metronic/layout/core'
import { getSetupGroups, saveSetupGroup, deleteSetupGroup } from '../generic/crud'
import { defaultCamera, defaultWeighbridge } from './parserPresets'
import BedRockContext from '../../BedRockContext'
import { showToast, TOAST_SUCCESS, TOAST_FAILURE } from '../../utils/toast/Toast'
import SetupGroupCard from './components/SetupGroupCard'

const groupId = (g) => g && (g.primaryId || g.id)

// Combined "Setup Groups" page. Each group pairs ONE camera + ONE weighbridge; a site
// with multiple weighing scales can capture from a chosen pair. Built on /weighbridge-setup.
const WeighbridgeSetupPage = () => {
    const myData = useContext(BedRockContext)
    const [unlocked, setUnlocked] = useState(false)
    const [enteredPassword, setEnteredPassword] = useState('')
    const [passwordError, setPasswordError] = useState('')
    const [loading, setLoading] = useState(true)
    const [adding, setAdding] = useState(false)
    const [groups, setGroups] = useState([])
    const [expandedId, setExpandedId] = useState(null)
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleting, setDeleting] = useState(false)

    useEffect(() => {
        myData.setBaseData({ toolBarButtons: [] })
        if (unlocked) fetchGroups()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unlocked])

    const fetchGroups = async () => {
        try {
            setLoading(true)
            const response = await getSetupGroups()
            const list = response.data || []
            setGroups(list)
            if (list.length > 0 && expandedId == null) setExpandedId(groupId(list[0]))
        } catch (err) {
            console.error('Error fetching setup groups:', err)
            showToast('Failed to load setup groups', TOAST_FAILURE)
        } finally {
            setLoading(false)
        }
    }

    // Save (upsert) a group. Returns the saved group so the card can resolve its new id.
    const handleSaveGroup = async (group) => {
        try {
            const saved = (await saveSetupGroup(group)).data
            setGroups(prev => {
                const gid = groupId(saved)
                const exists = prev.some(g => groupId(g) === gid)
                return exists ? prev.map(g => (groupId(g) === gid ? saved : g)) : [...prev, saved]
            })
            showToast('Setup group saved', TOAST_SUCCESS)
            return saved
        } catch (err) {
            console.error('Error saving setup group:', err)
            showToast('Failed to save setup group', TOAST_FAILURE)
            return null
        }
    }

    // Create a new group with default camera + weighbridge and persist immediately so it
    // gets an id (the serial monitor needs one).
    const handleAddGroup = async () => {
        try {
            setAdding(true)
            const newGroup = { name: `Scale ${groups.length + 1}`, enabled: true, camera: defaultCamera(), weighbridge: defaultWeighbridge() }
            const saved = (await saveSetupGroup(newGroup)).data
            setGroups(prev => [...prev, saved])
            setExpandedId(groupId(saved))
            showToast('Setup group added', TOAST_SUCCESS)
        } catch (err) {
            console.error('Error adding setup group:', err)
            showToast('Failed to add setup group', TOAST_FAILURE)
        } finally {
            setAdding(false)
        }
    }

    const handleConfirmDelete = async () => {
        if (!deleteTarget) return
        const gid = groupId(deleteTarget)
        try {
            setDeleting(true)
            if (gid) await deleteSetupGroup(gid)
            setGroups(prev => prev.filter(g => groupId(g) !== gid))
            showToast('Setup group deleted', TOAST_SUCCESS)
            setDeleteTarget(null)
        } catch (err) {
            console.error('Error deleting setup group:', err)
            showToast('Failed to delete setup group', TOAST_FAILURE)
        } finally {
            setDeleting(false)
        }
    }

    const breadcrumbs = [
        { title: 'Home', path: '/dashboard', isSeparator: false, isActive: false },
        { title: '', path: '', isSeparator: true, isActive: false },
        { title: 'Organization Settings', path: '/org-settings', isSeparator: false, isActive: false },
        { title: '', path: '', isSeparator: true, isActive: false },
    ]

    // Password gate — block access until correct password is entered
    if (!unlocked) {
        const handlePasswordSubmit = () => {
            if (enteredPassword === 'Hardware') { setUnlocked(true); setPasswordError('') }
            else setPasswordError('Incorrect password')
        }
        return (
            <>
                <PageTitle breadcrumbs={breadcrumbs}>Setup Groups</PageTitle>
                <div className="px-4 px-lg-6 d-flex justify-content-center align-items-center" style={{ minHeight: '40vh' }}>
                    <Card className="border-0 shadow-sm" style={{ maxWidth: '380px', width: '100%' }}>
                        <Card.Body className="p-4 text-center">
                            <div className="bg-primary bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3" style={{ width: 56, height: 56 }}>
                                <i className="bi bi-lock fs-3 text-primary"></i>
                            </div>
                            <h5 className="mb-1">Password Required</h5>
                            <p className="text-muted small mb-3">Enter password to access Setup Groups</p>
                            <Form.Control type="password" size="sm" value={enteredPassword}
                                onChange={(e) => { setEnteredPassword(e.target.value); setPasswordError('') }}
                                onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                                placeholder="Enter password" isInvalid={!!passwordError} autoFocus className="mb-2" />
                            <Form.Control.Feedback type="invalid" className="text-start">{passwordError}</Form.Control.Feedback>
                            <Button variant="primary" size="sm" className="w-100 mt-1" onClick={handlePasswordSubmit}>
                                <i className="bi bi-unlock me-1"></i>Unlock
                            </Button>
                        </Card.Body>
                    </Card>
                </div>
            </>
        )
    }

    return (
        <>
            <PageTitle breadcrumbs={breadcrumbs}>Setup Groups</PageTitle>
            <div className="px-4 px-lg-6">
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h4 className="mb-1">Setup Groups</h4>
                        <p className="text-muted small mb-0">Each group pairs one camera with one weighbridge. Configure both here.</p>
                    </div>
                    <Button variant="primary" size="sm" onClick={handleAddGroup} disabled={adding}>
                        {adding ? <><Spinner size="sm" animation="border" className="me-1" />Adding</> : <><i className="bi bi-plus-lg me-1"></i>Add Group</>}
                    </Button>
                </div>

                {loading ? (
                    <div className="text-center py-5">
                        <Spinner animation="border" variant="primary" size="sm" />
                        <p className="text-muted small mt-2 mb-0">Loading setup groups...</p>
                    </div>
                ) : groups.length === 0 ? (
                    <Alert variant="light" className="border py-4 text-center">
                        <i className="bi bi-collection fs-3 text-muted d-block mb-2"></i>
                        <p className="mb-1">No setup groups yet.</p>
                        <p className="text-muted small mb-0">Click <strong>Add Group</strong> to configure your first camera + weighbridge pair.</p>
                    </Alert>
                ) : (
                    groups.map(group => (
                        <SetupGroupCard
                            key={groupId(group)}
                            group={group}
                            expanded={expandedId === groupId(group)}
                            onToggle={() => setExpandedId(expandedId === groupId(group) ? null : groupId(group))}
                            onSave={handleSaveGroup}
                            onDelete={setDeleteTarget}
                        />
                    ))
                )}
            </div>

            {/* Delete confirmation */}
            <Modal show={!!deleteTarget} onHide={() => !deleting && setDeleteTarget(null)} centered size="sm">
                <Modal.Header closeButton className="py-3 px-4 border-bottom bg-danger bg-opacity-10">
                    <Modal.Title className="fs-6"><i className="bi bi-exclamation-triangle me-2 text-danger"></i>Delete Group</Modal.Title>
                </Modal.Header>
                <Modal.Body className="px-4 py-3">
                    <p className="small mb-0">Delete <strong>{deleteTarget?.name || 'this group'}</strong>? This removes its camera and weighbridge configuration.</p>
                </Modal.Body>
                <Modal.Footer className="py-2 px-4 border-top">
                    <Button variant="light" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>Cancel</Button>
                    <Button variant="danger" size="sm" onClick={handleConfirmDelete} disabled={deleting}>
                        {deleting ? <><Spinner size="sm" animation="border" className="me-1" />Deleting</> : 'Delete'}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    )
}

export { WeighbridgeSetupPage }
